import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Users, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { findSimilarContacts } from "@/lib/contactSimilarity";
import { SimilarContactsWarning } from "@/components/ContactPicker";

const EMPTY = { contact_gender: "", contact_name: "", contact_email: "", role: "", phone: "", is_primary: false };

function ContactForm({ draft, setDraft, others, onSave, onCancel, saving }) {
  const similar = useMemo(() => findSimilarContacts(others, draft), [others, draft]);
  const f = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  return (
    <div data-testid="contact-form" className="rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-[90px_1fr_1fr] gap-2">
        <select data-testid="contact-form-gender" value={draft.contact_gender} onChange={f("contact_gender")}
          className="h-9 border border-zinc-200 rounded-md px-2 text-sm bg-white">
          <option value="">—</option><option value="M.">M.</option><option value="Mme.">Mme.</option><option value="M./Mme">M./Mme</option>
        </select>
        <Input data-testid="contact-form-name" placeholder="Nom" value={draft.contact_name} onChange={f("contact_name")} />
        <Input data-testid="contact-form-email" type="email" placeholder="Email" value={draft.contact_email} onChange={f("contact_email")} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input data-testid="contact-form-role" placeholder="Fonction (ex. DAF)" value={draft.role} onChange={f("role")} />
        <Input data-testid="contact-form-phone" placeholder="Téléphone" value={draft.phone} onChange={f("phone")} />
      </div>
      <SimilarContactsWarning matches={similar} testid="contact-form-similar-warning" />
      <div className="flex items-center justify-between gap-2 pt-1">
        <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
          <input type="checkbox" data-testid="contact-form-primary" checked={!!draft.is_primary}
            onChange={(e) => setDraft({ ...draft, is_primary: e.target.checked })} />
          Contact principal
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} data-testid="contact-form-cancel"
            className="inline-flex items-center gap-1 px-3 h-8 text-xs rounded-md hover:bg-zinc-200 transition-colors"><X size={12} /> Annuler</button>
          <button type="button" onClick={onSave} disabled={saving} data-testid="contact-form-save"
            className={`inline-flex items-center gap-1 px-3 h-8 text-xs rounded-md text-white disabled:opacity-50 transition-colors ${similar.length ? "bg-amber-600 hover:bg-amber-700" : "bg-zinc-950 hover:bg-zinc-800"}`}>
            <Check size={12} /> {similar.length ? "Enregistrer malgré la similarité" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactRow({ c, onEdit, onDelete, onPrimary }) {
  return (
    <div data-testid={`contact-row-${c.contact_id}`} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0">
      <button type="button" onClick={onPrimary} title={c.is_primary ? "Contact principal" : "Définir comme principal"}
        data-testid={`contact-primary-${c.contact_id}`} className="shrink-0">
        <Star size={14} className={c.is_primary ? "fill-amber-400 text-amber-500" : "text-zinc-300 hover:text-amber-400"} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-900 truncate">
          {[c.contact_gender, c.contact_name].filter(Boolean).join(" ") || "—"}
          {c.role && <span className="text-zinc-500 text-xs"> · {c.role}</span>}
        </div>
        <div className="text-[11px] font-mono text-zinc-500 truncate">{[c.contact_email, c.phone].filter(Boolean).join(" · ")}</div>
      </div>
      <button type="button" onClick={onEdit} data-testid={`contact-edit-${c.contact_id}`} className="p-1.5 rounded hover:bg-zinc-100"><Pencil size={13} /></button>
      <button type="button" onClick={onDelete} data-testid={`contact-delete-${c.contact_id}`} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={13} /></button>
    </div>
  );
}

export default function ClientContactsManager({ clientId }) {
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | contact_id
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/clients/${clientId}/contacts`).then((r) => setContacts(r.data?.contacts || [])).catch(() => setContacts([]));
  }, [clientId]);

  const call = async (fn) => {
    setSaving(true);
    try {
      const r = await fn();
      setContacts([...(r.data?.contacts || [])].sort((a, b) => b.is_primary - a.is_primary));
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  const base = `/clients/${clientId}/contacts`;
  const save = () => call(() => (editing === "new" ? api.post(base, draft) : api.patch(`${base}/${editing}`, draft)));
  const open = (c) => { setEditing(c ? c.contact_id : "new"); setDraft(c ? { ...EMPTY, ...c } : { ...EMPTY, is_primary: !contacts.length }); };
  const others = contacts.filter((c) => c.contact_id !== editing);

  return (
    <div data-testid="client-contacts-manager" className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono flex items-center gap-1.5"><Users size={12} /> Contacts ({contacts.length})</div>
        {editing === null && (
          <button type="button" onClick={() => open(null)} data-testid="contact-add-btn"
            className="inline-flex items-center gap-1 px-2.5 h-7 text-xs rounded-md border border-zinc-200 hover:bg-zinc-50 transition-colors"><Plus size={12} /> Ajouter un contact</button>
        )}
      </div>
      {contacts.length === 0 && editing === null && <p className="text-xs text-zinc-400 italic" data-testid="contacts-empty">Aucun contact enregistré.</p>}
      {contacts.map((c) => (editing === c.contact_id
        ? <ContactForm key={c.contact_id} draft={draft} setDraft={setDraft} others={others} onSave={save} onCancel={() => setEditing(null)} saving={saving} />
        : <ContactRow key={c.contact_id} c={c} onEdit={() => open(c)}
            onPrimary={() => !c.is_primary && call(() => api.patch(`${base}/${c.contact_id}`, { is_primary: true }))}
            onDelete={() => window.confirm(`Supprimer le contact « ${c.contact_name || c.contact_email} » ?`) && call(() => api.delete(`${base}/${c.contact_id}`))} />))}
      {editing === "new" && <ContactForm draft={draft} setDraft={setDraft} others={others} onSave={save} onCancel={() => setEditing(null)} saving={saving} />}
    </div>
  );
}
